import React from "react";
import {
  CardContent,
  Paper,
  Button,
  IconButton,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Chip,
  LinearProgress,
  useTheme,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import DescriptionIcon from "@material-ui/icons/Description";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import ImageIcon from "@material-ui/icons/Image";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import { LoadingButton } from "@material-ui/lab";

import { useFileUpload } from "../hooks/useFileUpload";
import { AlertaModalV2 } from "src/components/AlertaSwall";
import { capitalizarPalabras } from "src/utils/sharedFunctions";

const useStyles = makeStyles((theme) => ({
  expandedContent: {
    backgroundColor: theme.palette.grey[50],
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  section: {
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  descripcionFull: {
    backgroundColor: "white",
    padding: theme.spacing(2),
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
    lineHeight: 1.6,
  },
  uploadZone: {
    backgroundColor: "white",
    border: `2px dashed ${theme.palette.primary.main}`,
    borderRadius: 6,
    padding: theme.spacing(2),
    textAlign: "center",
    transition: "all 0.2s ease",
    cursor: "pointer",
    "&:hover": {
      borderColor: theme.palette.primary.dark,
      backgroundColor: theme.palette.grey[50],
    },
  },
  uploadZoneActive: {
    borderColor: theme.palette.primary.dark,
    backgroundColor: theme.palette.primary.light + "10",
  },
  selectedFileChip: {
    margin: theme.spacing(1, 0),
    backgroundColor: theme.palette.primary.light + "20",
    "& .MuiChip-deleteIcon": {
      color: theme.palette.error.main,
    },
  },
  uploadButton: {
    borderRadius: 6,
  },
  fileList: {
    backgroundColor: "white",
    borderRadius: 6,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
  },
  fileItem: {
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.grey[50],
    },
  },
  fileIcon: {
    minWidth: 40,
  },
  emptyFileState: {
    textAlign: "center",
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
    backgroundColor: "white",
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: "0.875rem",
    marginTop: theme.spacing(1),
  },
  hidden: {
    display: "none",
  },
  uploadIcon: {
    fontSize: 32,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
  },
}));

// Función para obtener el ícono según el tipo de archivo
const getFileIcon = (fileName) => {
  const extension = fileName.split(".").pop()?.toLowerCase();

  switch (extension) {
    case "pdf":
      return <PictureAsPdfIcon style={{ color: "#f44336" }} />;
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
    case "svg":
      return <ImageIcon style={{ color: "#4caf50" }} />;
    case "doc":
    case "docx":
      return <DescriptionIcon style={{ color: "#2196f3" }} />;
    default:
      return <InsertDriveFileIcon style={{ color: "#757575" }} />;
  }
};

export default function PortafolioCardContent({
  portafolio,
  cliente,
  puedeModificar,
  onDeleteFile,
  deletingFile,
}) {
  const classes = useStyles();
  const theme = useTheme();

  const {
    selectedFile,
    uploading,
    uploadError,
    handleFileSelect,
    clearSelectedFile,
    uploadFile,
  } = useFileUpload(cliente.rut);

  const handleUpload = async () => {
    await uploadFile(portafolio.id);
  };

  const handleDeleteFile = async (archivo) => {
    const confirmacion = await AlertaModalV2({
      title: "¿Eliminar archivo?",
      text: `Se eliminará el archivo "${archivo.nombreArchivo}". Esta acción no se puede deshacer.`,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      icon: "warning",
    }).fire();
    if (!confirmacion.isConfirmed) {
      return;
    }

    await onDeleteFile(archivo);
  };

  const archivoCount = portafolio.archivosPortafolio?.length || 0;

  return (
    <CardContent className={classes.expandedContent}>
      {/* Descripción completa */}
      <div className={classes.section}>
        <Typography variant="subtitle2" className={classes.sectionTitle}>
          <DescriptionIcon fontSize="small" />
          Descripción Completa
        </Typography>
        <div className={classes.descripcionFull}>
          <Typography variant="body2">{portafolio.descripcion}</Typography>
        </div>
      </div>

      {/* Sección de archivos */}
      <div className={classes.section}>
        <Typography variant="subtitle2" className={classes.sectionTitle}>
          <AttachFileIcon fontSize="small" />
          Archivos Adjuntos ({archivoCount})
        </Typography>

        {/* Upload de archivos: el autor dentro del plazo, o un admin */}
        {puedeModificar && (
          <Box marginBottom={2}>
            <input
              className={classes.hidden}
              id={`file-input-${portafolio.id}`}
              type="file"
              onChange={handleFileSelect}
              accept="*/*"
            />

            <label htmlFor={`file-input-${portafolio.id}`}>
              <div
                className={`${classes.uploadZone} ${
                  selectedFile ? classes.uploadZoneActive : ""
                }`}
              >
                <CloudUploadIcon className={classes.uploadIcon} />
                <Typography variant="body2" gutterBottom>
                  {selectedFile
                    ? "Archivo seleccionado"
                    : "Subir nuevo archivo"}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {selectedFile
                    ? 'Haz clic en "Subir" para confirmar'
                    : "Haz clic aquí para seleccionar un archivo"}
                </Typography>
              </div>
            </label>

            {selectedFile && (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                marginTop={2}
              >
                <Chip
                  label={selectedFile.name}
                  onDelete={clearSelectedFile}
                  className={classes.selectedFileChip}
                  variant="outlined"
                />
                <LoadingButton
                  loading={uploading}
                  onClick={handleUpload}
                  variant="contained"
                  color="primary"
                  className={classes.uploadButton}
                  startIcon={<CloudUploadIcon />}
                  size="small"
                >
                  Subir
                </LoadingButton>
              </Box>
            )}

            {uploading && (
              <Box marginTop={1}>
                <LinearProgress />
                <Typography variant="caption" color="textSecondary">
                  Subiendo archivo...
                </Typography>
              </Box>
            )}

            {uploadError && (
              <Typography className={classes.errorText}>
                {uploadError}
              </Typography>
            )}

            <Divider style={{ margin: "16px 0" }} />
          </Box>
        )}

        {/* Lista de archivos */}
        {archivoCount > 0 ? (
          <Paper className={classes.fileList}>
            <List dense>
              {portafolio.archivosPortafolio.map((archivo, index) => (
                <React.Fragment key={`archivo_${archivo.id || index}`}>
                  <ListItem className={classes.fileItem}>
                    <ListItemIcon className={classes.fileIcon}>
                      {getFileIcon(archivo.nombreArchivo)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Button
                          onClick={() =>
                            window.open(archivo.urlArchivo, "_blank")
                          }
                          style={{
                            textTransform: "none",
                            justifyContent: "flex-start",
                            padding: 0,
                            minWidth: "auto",
                            color: theme.palette.primary.main,
                            fontWeight: 400,
                          }}
                        >
                          {archivo.nombreArchivo}
                        </Button>
                      }
                      secondary={`Subido por ${
                        capitalizarPalabras(
                          portafolio.ultimaModificacion?.empleado,
                        ) || "Usuario"
                      }`}
                    />
                    {puedeModificar && (
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => handleDeleteFile(archivo)}
                          color="secondary"
                          size="small"
                        >
                          {!deletingFile ? (
                            <DeleteIcon fontSize="small" />
                          ) : (
                            <CircularProgress size={20} />
                          )}{" "}
                        </IconButton>
                      </ListItemSecondaryAction>
                    )}
                  </ListItem>
                  {index < portafolio.archivosPortafolio.length - 1 && (
                    <Divider />
                  )}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        ) : (
          <div className={classes.emptyFileState}>
            <AttachFileIcon
              style={{ fontSize: 32, color: "#ccc", marginBottom: 8 }}
            />
            <Typography variant="body2" gutterBottom>
              No hay archivos adjuntos
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {puedeModificar
                ? "Sube archivos usando la zona de arriba"
                : "Solo el autor puede adjuntar archivos, y hasta 3 días después de crear la bitácora"}
            </Typography>
          </div>
        )}
      </div>
    </CardContent>
  );
}
